// Privacy Policy content, mirrored from https://www.phyber.care/legal/privacy
// Inline styles are used because Tailwind's preflight resets heading and list styles.

export const COLOR_TEXT = "#334155";
export const COLOR_HEADING = "#171717";

export const h = (text) =>
  `<h3 style="font-size:16px;font-weight:600;color:${COLOR_HEADING};margin:24px 0 8px;">${text}</h3>`;
const sub = (text) =>
  `<h4 style="font-size:14px;font-weight:600;color:${COLOR_HEADING};margin:16px 0 6px;">${text}</h4>`;
export const p = (text) =>
  `<p style="font-size:14px;line-height:1.6;color:${COLOR_TEXT};margin:0 0 10px;">${text}</p>`;
export const ul = (items) =>
  `<ul style="list-style:disc;padding-left:22px;margin:0 0 10px;font-size:14px;line-height:1.6;color:${COLOR_TEXT};">${items
    .map((item) => `<li>${item}</li>`)
    .join("")}</ul>`;
export const link = (href, text = href) =>
  `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:#2563EB;">${text}</a>`;
export const mail = (email) => `<a href="mailto:${email}" style="color:#2563EB;">${email}</a>`;

const cellStyle = `border:1px solid #E2E8F0;padding:8px 12px;text-align:left;vertical-align:top;`;
const table = (rows) =>
  `<table style="border-collapse:collapse;width:100%;margin:0 0 10px;font-size:14px;line-height:1.5;color:${COLOR_TEXT};">
    <thead><tr>
      <th style="${cellStyle}background:#F8FAFC;color:${COLOR_HEADING};font-weight:600;">Permission</th>
      <th style="${cellStyle}background:#F8FAFC;color:${COLOR_HEADING};font-weight:600;">Purpose</th>
    </tr></thead>
    <tbody>${rows
      .map(([perm, purpose]) => `<tr><td style="${cellStyle}">${perm}</td><td style="${cellStyle}">${purpose}</td></tr>`)
      .join("")}</tbody>
  </table>`;

export const privacyPolicyHtml = [
  `<h2 style="font-size:20px;font-weight:700;color:${COLOR_HEADING};margin:0 0 4px;">Privacy Policy</h2>`,
  p("How Phyber collects, uses, protects, and manages personal and health information across its digital wellbeing ecosystem."),
  `<p style="font-size:13px;color:#64748B;margin:0 0 16px;">Effective Date: 21 July 2026 · Last Updated: 21 July 2026</p>`,

  h("1. Introduction"),
  p(`Welcome to Phyber, a digital healthcare platform operated by Northoak Technologies Private Limited ("Phyber", "Company", "we", "our", or "us").`),
  p("Phyber is an integrated healthcare and wellness platform that enables individuals, healthcare professionals, employers, educational institutions, and partner organizations to access healthcare services, mental wellness support, assessments, appointments, health records, digital care journeys, and other related services through our websites, mobile applications, APIs and affiliated platforms (collectively, the Services)."),
  p("This Privacy Policy explains:"),
  ul([
    "What information we collect",
    "Why we collect it",
    "How we use it",
    "Who we share it with",
    "How we protect it",
    "Your privacy rights and choices",
  ]),
  p("By accessing or using our Services, you acknowledge that you have read and understood this Privacy Policy."),

  h("2. Who We Are"),
  p("<strong>Northoak Technologies Private Limited</strong>"),
  p("Registered Office:"),
  p("Flat No. SH-1106, H Block, Purva Seasons<br />C. V. Raman Nagar<br />Bengaluru, Karnataka 560093<br />India"),
  p(`Email: ${mail("business@phyber.care")}`),
  p(`Website: ${link("https://phyber.care")}`),

  h("3. Scope"),
  p("This Privacy Policy applies to all products and services operated by Northoak Technologies Private Limited, including but not limited to:"),
  ul([
    "Phyber mobile applications",
    "Phyber web platform",
    "Newraun mental wellness platform",
    "Corporate wellness platform",
    "University wellness platform",
    "Healthcare provider portals",
    "APIs",
    "Customer support channels",
    "Future products released under the Phyber ecosystem unless governed by a separate privacy notice",
  ]),

  h("4. Information We Collect"),
  p("The categories of information we collect depend on the Services you use."),
  sub("4.1 Identity Information"),
  ul([
    "Name",
    "Date of birth",
    "Gender",
    "Government-issued identification where legally required",
    "Profile photograph",
    "Username",
    "Account credentials",
  ]),
  sub("4.2 Contact Information"),
  ul(["Email address", "Mobile number", "Postal address", "Emergency contact information"]),
  sub("4.3 Health Information"),
  p("Where you choose to use healthcare services, we may collect information including:"),
  ul([
    "Medical history",
    "Existing conditions",
    "Symptoms",
    "Allergies",
    "Medications",
    "Prescriptions",
    "Diagnostic reports",
    "Laboratory reports",
    "Consultation notes",
    "Vaccination information",
    "Wellness goals",
    "Lifestyle information",
    "Insurance information where required for healthcare services",
  ]),
  p("Health information is processed solely for providing healthcare, wellness and related services requested by you or your organization, except where otherwise required by law."),
  sub("4.4 Mental Wellness Information"),
  p("Where you use Newraun or other mental wellness services, we may collect:"),
  ul([
    "Assessment responses",
    "Psychometric scores",
    "Risk classifications",
    "Mood journals",
    "Guided journey progress",
    "Counselling records",
    "Therapy notes created by healthcare professionals",
    "Wellness check-ins",
    "Behavioural indicators",
    "Clinical observations where applicable",
  ]),
  p("This information is treated as sensitive personal information."),
  sub("4.5 Appointment Information"),
  ul([
    "Appointment history",
    "Healthcare provider selected",
    "Clinic or hospital selected",
    "Visit history",
    "Consultation timings",
    "Appointment preferences",
    "Cancellation records",
  ]),
  sub("4.6 Location Information"),
  p("With your explicit permission, Phyber may access your device's approximate or precise location to:"),
  ul([
    "Display nearby clinics, hospitals, laboratories, pharmacies and healthcare providers",
    "Recommend healthcare services near you",
    "Estimate travel distance and travel time",
    "Improve appointment recommendations",
    "Enable emergency or urgent care features",
    "Personalize location-based healthcare experiences",
    "Support future location-enabled healthcare services",
  ]),
  p("Location access is optional."),
  p("You may deny or revoke location permission at any time through your device settings."),
  p("If location permission is not granted, location-dependent features may not function as intended."),
  p("Phyber does not continuously track your location in the background unless you explicitly enable a feature that requires such access and your device permissions permit it."),
  sub("4.7 Device Information"),
  p("We may automatically collect:"),
  ul([
    "Device model",
    "Operating system",
    "Browser",
    "IP address",
    "Device identifiers",
    "App version",
    "Network information",
    "Language preferences",
    "Diagnostic information",
    "Crash reports",
  ]),
  sub("4.8 Usage Information"),
  p("We may collect information regarding your interaction with the Services, including:"),
  ul([
    "Pages visited",
    "Features accessed",
    "Search activity",
    "Session duration",
    "Clickstream information",
    "Error logs",
    "Performance metrics",
  ]),
  sub("4.9 Communications"),
  p("We may retain communications including:"),
  ul([
    "Customer support requests",
    "Emails",
    "Chat conversations",
    "Feedback",
    "Surveys",
    "Call recordings where legally permitted and disclosed",
  ]),

  h("5. Permissions Requested by the App"),
  p("Depending on the features you choose to use, Phyber may request access to:"),
  table([
    ["Location", "Nearby healthcare providers and location-based services"],
    ["Camera", "Capture prescriptions, reports and profile photos"],
    ["Photo Library", "Upload health records and documents"],
    ["Microphone", "Teleconsultations and counselling sessions"],
    ["Notifications", "Appointment reminders, wellness reminders and service updates"],
    ["Calendar", "Save appointments to your calendar (optional)"],
    ["Contacts", "Emergency contact selection or invitation features (optional)"],
  ]),
  p("Permissions are requested only when required for a feature you actively use."),
  p("You may disable permissions at any time through your device settings."),

  h("6. How We Use Your Information"),
  p("We use your information to:"),
  ul([
    "Create and manage your account",
    "Verify your identity",
    "Deliver healthcare services",
    "Facilitate appointments",
    "Connect you with healthcare providers",
    "Conduct psychometric assessments",
    "Deliver counselling services",
    "Generate assessment reports",
    "Recommend healthcare providers",
    "Recommend nearby healthcare facilities",
    "Personalize your experience",
    "Improve healthcare outcomes",
    "Provide customer support",
    "Send reminders and notifications",
    "Detect fraud and misuse",
    "Protect platform security",
    "Conduct analytics using aggregated or anonymized information",
    "Comply with applicable laws",
    "Develop new healthcare products and features",
  ]),
  p("We do not use your personal health information for advertising without your explicit consent."),

  h("7. Artificial Intelligence"),
  p("Certain Services may use artificial intelligence, machine learning or automated processing to:"),
  ul([
    "Personalize wellness journeys",
    "Generate wellness insights",
    "Assist healthcare workflows",
    "Recommend content",
    "Improve service quality",
    "Support healthcare professionals",
  ]),
  p("AI-generated insights are intended solely as decision-support tools."),
  p("They do not constitute medical advice and should not replace consultation with qualified healthcare professionals."),

  h("8. Legal Basis for Processing"),
  p("Depending on applicable law, we process personal information based on:"),
  ul([
    "Your consent",
    "Performance of a contract",
    "Compliance with legal obligations",
    "Legitimate business interests",
    "Protection of vital interests",
    "Provision of healthcare services",
  ]),
  p("You may withdraw consent at any time where processing is based solely on consent."),

  h("9. Sharing of Information"),
  p("We may share information with:"),
  sub("Healthcare Providers"),
  p("Doctors, counsellors, psychologists, therapists, clinics, hospitals and healthcare organizations providing services requested by you."),
  sub("Corporate and Educational Clients"),
  p("Where your account is sponsored by an employer, university or educational institution, limited information may be shared in accordance with applicable agreements."),
  p("Sensitive health information will not be disclosed without appropriate authorization, except where required by law or provided in anonymized or aggregated form."),
  sub("Service Providers"),
  p("Cloud hosting providers, communication providers, payment processors, analytics providers, authentication providers, customer support providers and other vendors supporting the Services."),
  sub("Insurance and Healthcare Partners"),
  p("Where you request insurance, claims, reimbursement or integrated healthcare services."),
  sub("Government Authorities"),
  p("Where disclosure is required by applicable law, regulation or lawful request."),
  sub("Business Transfers"),
  p("In connection with mergers, acquisitions, restructuring or sale of assets."),

  h("10. Third-Party Technologies"),
  p("Our Services may integrate with:"),
  ul([
    "Cloud infrastructure providers",
    "Mapping services",
    "Payment gateways",
    "Authentication providers",
    "Push notification services",
    "Analytics providers",
    "Crash reporting services",
    "Healthcare information exchanges",
    "Government digital health systems",
    "Insurance partners",
  ]),
  p("Each provider processes information according to its own privacy obligations and applicable agreements."),

  h("11. Analytics and Diagnostics"),
  p("We may use analytics and diagnostics tools to:"),
  ul([
    "Understand application performance",
    "Improve usability",
    "Identify bugs",
    "Monitor service reliability",
    "Measure feature adoption",
  ]),
  p("Where possible, analytics are collected using aggregated or pseudonymized information."),

  h("12. Data Security"),
  p("We implement administrative, technical and organizational safeguards including:"),
  ul([
    "Encryption in transit using TLS",
    "Encryption of sensitive information at rest where appropriate",
    "Secure authentication",
    "Role-based access controls",
    "Multi-factor authentication for administrative access where applicable",
    "Audit logging",
    "Security monitoring",
    "Regular vulnerability assessments",
    "Secure backup procedures",
    "Disaster recovery planning",
  ]),
  p("No electronic system can be guaranteed to be completely secure; however, we continually work to protect your information using industry-standard practices."),

  h("13. Data Retention"),
  p("We retain information only for as long as necessary to:"),
  ul([
    "Provide the Services",
    "Maintain healthcare records where applicable",
    "Fulfil contractual obligations",
    "Resolve disputes",
    "Detect fraud",
    "Comply with legal and regulatory obligations",
  ]),
  p("Retention periods vary depending on the nature of the information and applicable law."),

  h("14. International Data Transfers"),
  p("Your information may be processed or stored in jurisdictions outside your state or country."),
  p("Where cross-border transfers occur, we implement reasonable safeguards consistent with applicable law."),

  h("15. Your Rights"),
  p("Subject to applicable law, you may have the right to:"),
  ul([
    "Access your personal information",
    "Correct inaccurate information",
    "Update information",
    "Request deletion",
    "Withdraw consent",
    "Restrict processing",
    "Object to certain processing",
    "Receive a copy of your information where legally applicable",
    "Lodge complaints with applicable regulatory authorities",
  ]),

  h("16. Account Deletion"),
  p("You may request deletion of your account through:"),
  ul([
    "In-app account deletion",
    "Account settings (where available)",
    "Customer support",
    "Email to our Privacy Officer",
  ]),
  p("Upon receiving a valid request, we will delete or anonymize your personal information within a reasonable period unless retention is required by law, regulatory obligations, fraud prevention or healthcare record requirements."),

  h("17. Cookies and Similar Technologies"),
  p("Our websites and applications may use cookies, local storage, SDKs and similar technologies to:"),
  ul([
    "Maintain user sessions",
    "Remember preferences",
    "Improve functionality",
    "Measure performance",
    "Analyze usage",
    "Enhance security",
  ]),
  p("You may control cookies through your browser settings where applicable."),

  h("18. Children's Privacy"),
  p("Our Services are not directed to children under the age permitted by applicable law without appropriate parental, guardian or institutional authorization."),
  p("Where required, parental or guardian consent will be obtained before collecting personal information from minors."),

  h("19. Data Breach Response"),
  p("If we become aware of a personal data breach that is likely to result in significant harm, we will take appropriate steps to investigate, mitigate the impact and notify affected individuals and relevant authorities where required by applicable law."),

  h("20. Changes to this Privacy Policy"),
  p("We may update this Privacy Policy periodically to reflect changes in our Services, technology or legal requirements."),
  p("Material changes will be communicated through our applications, websites or other appropriate channels before they become effective where required by law."),

  h("21. Contact Us"),
  p("For questions regarding this Privacy Policy or our privacy practices, please contact:"),
  p("<strong>Privacy Officer</strong>"),
  p("Northoak Technologies Private Limited"),
  p(`Support: ${mail("support@phyber.care")}`),
  p(`Website: ${link("https://phyber.care")}`),

  h("22. Compliance Statement"),
  p("Phyber is committed to protecting personal information in accordance with applicable privacy and data protection laws. We continually review and improve our privacy practices to align with evolving legal, regulatory and industry standards, including the Digital Personal Data Protection Act, 2023 (India), and internationally recognized privacy principles where applicable."),
  p("This Privacy Policy should be read together with our Terms of Service, Consent for Health Data Processing, Cookie Policy and any service-specific privacy notices made available within the Phyber ecosystem."),

  h("Contact Us"),
  p("<strong>Northoak Technologies Private Limited</strong>"),
  p(`Email: ${mail("business@phyber.care")}`),
  p(`Support: ${mail("support@phyber.care")}`),
  p(`Website: ${link("https://phyber.care")}`),
  p("SH-1106, Purva Seasons, C. V. Raman Nagar, Bengaluru, Karnataka 560093, India"),
].join("\n");
